import * as stylex from "@stylexjs/stylex";
import { ChevronRight } from "lucide-react";
import { type ReactNode, useId } from "react";
import "@/theme/tokens.stylex";
import { colors, space } from "@/theme/tokens.stylex";
import "@/theme/type.stylex";
import { type } from "@/theme/type.stylex";
import { Icon } from "./Icon";
import { SwitchTrack } from "./Switch";
type SettingsRowProps = {
  title: ReactNode;
  description?: ReactNode;
  status?: ReactNode;
  children?: ReactNode;
  onClick?: () => void;
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  align?: "center" | "start";
  divider?: boolean;
  xstyle?: stylex.StyleXStyles;
};
const styles = {
  root: {
    k1xSpc: "x78zum5",
    kwnvtZ: "x1a02dak",
    kjj79g: "x1qughib",
    kGNEyG: "x6s0dn4",
    kOIVth: "x190un1r",
    k8WAf4: "xqp6ajr",
    kMwMTN: "x1a11ngh",
    $$css: true
  },
  start: {
    kGNEyG: "x1cy8zhl",
    $$css: true
  },
  divider: {
    kGVxlE: "x172rvlu",
    $$css: true
  },
  button: {
    kzqmXN: "xh8yej3",
    kMzoRj: "xc342km",
    kg3NbH: "xnjsko4",
    kWkggS: "xjbqb8w",
    kVVagm: "xln7xf2",
    k9WMMc: "x1yc453h",
    kkrTdU: "x1ypdohk",
    $$css: true
  }
};
export function SettingsRow({
  title,
  description,
  status,
  children,
  onClick,
  checked,
  onCheckedChange,
  align = "center",
  divider = true,
  xstyle
}: SettingsRowProps) {
  const id = useId();
  const rowStyles = [styles.root, align === "start" && styles.start, divider && styles.divider];
  const text = <span {...{
    0: {
      className: "x78zum5 xdt5ytf xt4j9g6 xfb3i0g"
    },
    2: {
      className: "x78zum5 xdt5ytf xt4j9g6 xfb3i0g xz9dl7a"
    },
    1: {
      className: "x78zum5 xdt5ytf xt4j9g6 xfb3i0g xk30mkq"
    },
    3: {
      className: "x78zum5 xdt5ytf xt4j9g6 xfb3i0g xz9dl7a xk30mkq"
    }
  }[!!(align === "start") << 1 | !!(status !== undefined) << 0]}>
      <span id={`${id}-title`} className="x1iig92y xhxcbl2 x1hrmo45">
        {title}
      </span>
      {description && <span id={`${id}-description`} className="x14c3fy2 x1dld7ex x1fzhlzt">
          {description}
        </span>}
      {status !== undefined && <span role="status" aria-live="polite" className="xr0iwhn xif65rj xkv8mnm xtvhhri xwn7fz2">
          {status}
        </span>}
    </span>;
  if (onCheckedChange) {
    return <button type="button" role="switch" aria-checked={checked ?? false} aria-labelledby={`${id}-title`} aria-describedby={description ? `${id}-description` : undefined} onClick={() => onCheckedChange(!checked)} {...stylex.props(...rowStyles, styles.button, xstyle)}>
        {text}
        <SwitchTrack checked={checked ?? false} />
      </button>;
  }
  if (onClick) {
    return <button type="button" onClick={onClick} {...stylex.props(...rowStyles, styles.button, xstyle)}>
        {text}
        <span className="x78zum5 x6s0dn4 x152posx x19d36u7 x15i68ds">
          {children}
          <Icon icon={ChevronRight} size={22} />
        </span>
      </button>;
  }
  return <div {...stylex.props(...rowStyles, xstyle)}>
      {text}
      <div className="x193iq5w x78zum5">{children}</div>
    </div>;
}